// İSG uzmanı portalı — Yetki matrisi (api-contract §9.3). Rol seç → Modül (satır) ×
// Aksiyon (sütun) grid; her hücre grant toggle. Kaydet → `PUT .../permissions`
// (sunucu farkı uygular + yetki cache invalidate → anında etkin). Sistem-admin rolü
// kilitli (FE önden engelle; backend `409 SYSTEM_ADMIN_ROLE_LOCKED`). isPublic/
// allAuthorized aksiyonlar grant gerektirmez → "Otomatik" (salt-okur), katalogdan.
//
// Erişilebilirlik: her hücre native checkbox (klavye/space), `aria-label` =
// "modül — aksiyon"; sütun başlıkları `scope=col`, ilk sütun (modül) sticky.

import {
  Alert,
  Breadcrumb,
  Button,
  ButtonSet,
  Card,
  CardContent,
  type MatrixModuleDto,
  PageHeader,
  type RolePermissionMatrixDto,
  Spinner,
  Tag,
  adminApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Save } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { adminModulesKey } from '../../lib/adminAccess';
import { adminKeys } from '../../lib/adminKeys';

const ACTION_ORDER = ['list', 'get', 'create', 'update', 'delete'];

function orderActionValues(values: string[]): string[] {
  return [...values].sort((a, b) => {
    const ia = ACTION_ORDER.indexOf(a);
    const ib = ACTION_ORDER.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
  });
}

/** Rolün mevcut grant durumunu düz bir haritaya indirger (actionUuid → granted). */
function toBaseline(matrix: RolePermissionMatrixDto): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const mod of matrix.modules) {
    for (const action of mod.actions) {
      out[action.uuid] = action.granted;
    }
  }
  return out;
}

export function RolePermissionsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { roleUuid = '' } = useParams();

  const matrixQuery = useQuery({
    queryKey: adminKeys.rolePermissions(roleUuid),
    queryFn: () => adminApi.getRolePermissions(roleUuid),
    enabled: roleUuid.length > 0,
  });

  // Katalog (isPublic/allAuthorized bayrakları) — erişim sondasıyla paylaşılan anahtar.
  const catalogQuery = useQuery({
    queryKey: adminModulesKey,
    queryFn: () => adminApi.getModuleCatalog(),
    staleTime: 5 * 60_000,
  });

  const baseline = useMemo(
    () => (matrixQuery.data ? toBaseline(matrixQuery.data) : {}),
    [matrixQuery.data],
  );

  const [grants, setGrants] = useState<Record<string, boolean>>({});
  const [message, setMessage] = useState<string | null>(null);

  // Veri (ilk yükleme veya kaydet sonrası invalidate) değişince yerel durumu senkronla.
  useEffect(() => {
    setGrants(baseline);
  }, [baseline]);

  // Katalogdan otomatik-geçen aksiyonlar (grant gerektirmez): actionUuid kümesi.
  const autoActions = useMemo(() => {
    const set = new Set<string>();
    for (const mod of catalogQuery.data ?? []) {
      for (const action of mod.actions) {
        if (action.isPublic || action.allAuthorized) {
          set.add(action.uuid);
        }
      }
    }
    return set;
  }, [catalogQuery.data]);

  const isLocked = matrixQuery.data?.isSystemAdmin ?? false;

  const changed = useMemo(() => {
    const entries: { actionUuid: string; granted: boolean }[] = [];
    for (const [uuid, granted] of Object.entries(grants)) {
      if (baseline[uuid] !== granted) {
        entries.push({ actionUuid: uuid, granted });
      }
    }
    return entries;
  }, [grants, baseline]);

  const saveMutation = useMutation({
    mutationFn: () => adminApi.updateRolePermissions(roleUuid, { permissions: changed }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.rolePermissions(roleUuid) });
      setMessage(t('admin.perm.saved'));
    },
  });

  // Sütun kümesi: tüm modüllerdeki aksiyon değerlerinin birleşimi (kanonik sıra).
  const columns = useMemo(() => {
    const values = new Set<string>();
    for (const mod of matrixQuery.data?.modules ?? []) {
      for (const action of mod.actions) {
        values.add(action.value);
      }
    }
    return orderActionValues([...values]);
  }, [matrixQuery.data]);

  if (matrixQuery.isPending) {
    return (
      <div className="flex min-h-[30vh] items-center justify-center">
        <Spinner label={t('admin.common.loading')} />
      </div>
    );
  }

  if (matrixQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        <Breadcrumb
          items={[
            { label: t('admin.nav'), href: '/admin' },
            { label: t('admin.tab.roles'), href: '/admin/roles' },
          ]}
        />
        <Alert tone="danger">{resolveApiErrorMessage(matrixQuery.error, t)}</Alert>
      </div>
    );
  }

  const matrix = matrixQuery.data;

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb
        items={[
          { label: t('admin.nav'), href: '/admin' },
          { label: t('admin.tab.roles'), href: '/admin/roles' },
          { label: matrix.roleName },
        ]}
      />
      <PageHeader
        title={t('admin.perm.title')}
        helperText={t('admin.perm.subtitle', { role: matrix.roleName })}
        actions={
          !isLocked ? (
            <ButtonSet>
              <Button
                variant="secondary"
                onClick={() => setGrants(baseline)}
                disabled={changed.length === 0 || saveMutation.isPending}
              >
                {t('admin.perm.reset')}
              </Button>
              <Button
                onClick={() => {
                  setMessage(null);
                  saveMutation.mutate();
                }}
                loading={saveMutation.isPending}
                disabled={changed.length === 0}
              >
                <Save className="h-4 w-4" aria-hidden="true" />
                {t('admin.perm.save')}
              </Button>
            </ButtonSet>
          ) : undefined
        }
      />

      {isLocked ? (
        <Alert tone="info">
          <span className="inline-flex items-center gap-2">
            <Lock className="h-4 w-4" aria-hidden="true" />
            {t('admin.perm.locked')}
          </span>
        </Alert>
      ) : null}
      {message ? <Alert tone="success">{message}</Alert> : null}
      {saveMutation.isError ? (
        <Alert tone="danger">{resolveApiErrorMessage(saveMutation.error, t)}</Alert>
      ) : null}

      <Card className="carbon:p-0">
        <CardContent className="carbon:p-0">
          <MatrixGrid
            modules={matrix.modules}
            columns={columns}
            grants={grants}
            autoActions={autoActions}
            locked={isLocked}
            onToggle={(actionUuid, next) => setGrants((prev) => ({ ...prev, [actionUuid]: next }))}
            actionLabel={(value) => t(`admin.action.${value}`, { defaultValue: value })}
            cellLabel={(moduleName, actionName) =>
              t('admin.perm.cellLabel', { module: moduleName, action: actionName })
            }
            moduleColLabel={t('admin.perm.col.module')}
            autoLabel={t('admin.perm.auto')}
            emptyLabel={t('admin.perm.na')}
          />
        </CardContent>
      </Card>

      <p className="text-xs text-muted">{t('admin.perm.legend')}</p>
    </div>
  );
}

interface MatrixGridProps {
  modules: MatrixModuleDto[];
  columns: string[];
  grants: Record<string, boolean>;
  autoActions: Set<string>;
  locked: boolean;
  onToggle: (actionUuid: string, next: boolean) => void;
  actionLabel: (value: string) => string;
  cellLabel: (moduleName: string, actionName: string) => string;
  moduleColLabel: string;
  autoLabel: string;
  emptyLabel: string;
}

function MatrixGrid({
  modules,
  columns,
  grants,
  autoActions,
  locked,
  onToggle,
  actionLabel,
  cellLabel,
  moduleColLabel,
  autoLabel,
  emptyLabel,
}: MatrixGridProps) {
  return (
    <div className="w-full overflow-x-auto rounded-lg border border-border">
      <table className="w-full border-collapse text-sm">
        <thead className="bg-surface-muted">
          <tr>
            <th
              scope="col"
              className="sticky left-0 z-[var(--z-base)] bg-surface-muted px-4 py-2.5 text-left text-xs font-semibold text-muted"
            >
              {moduleColLabel}
            </th>
            {columns.map((col) => (
              <th
                key={col}
                scope="col"
                className="px-3 py-2.5 text-center text-xs font-semibold text-muted"
              >
                {actionLabel(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {modules.map((mod) => {
            const byValue = new Map(mod.actions.map((a) => [a.value, a]));
            return (
              <tr key={mod.uuid} className="border-t border-border hover:bg-surface-muted/50">
                <th
                  scope="row"
                  className="sticky left-0 z-[var(--z-base)] whitespace-nowrap bg-surface px-4 py-2.5 text-left font-medium text-fg"
                >
                  {mod.name}
                  <span className="ml-2 text-xs font-normal text-muted">{mod.value}</span>
                </th>
                {columns.map((col) => {
                  const action = byValue.get(col);
                  if (!action) {
                    return (
                      <td
                        key={col}
                        className="px-3 py-2.5 text-center text-muted"
                        aria-hidden="true"
                      >
                        —<span className="sr-only">{emptyLabel}</span>
                      </td>
                    );
                  }
                  const isAuto = autoActions.has(action.uuid);
                  if (isAuto) {
                    return (
                      <td key={col} className="px-3 py-2.5 text-center">
                        <Tag color="gray" title={autoLabel}>
                          {autoLabel}
                        </Tag>
                      </td>
                    );
                  }
                  return (
                    <td key={col} className="px-3 py-2.5 text-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[var(--color-primary)] disabled:opacity-50"
                        checked={grants[action.uuid] ?? false}
                        disabled={locked}
                        aria-label={cellLabel(mod.name, action.name)}
                        onChange={(e) => onToggle(action.uuid, e.target.checked)}
                      />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
